import { useState } from 'react';
import type { BakimDurumu } from '../../data/system-status.js';
import { BRAND_MARK } from '../brand.js';

/**
 * Bakım ekranı — onaylanan B yönü (kartsız; logodan yayılan dalgalar, "nefes alan" logo, akan
 * ilerleme çizgisi) ve planlı bakımda 1 numara (ince çizgiler arasında "Başladı" ile "Tahmini
 * dönüş", sol kenarlı yönetici notu). Görsel prova: tasarım belgesi, 2026-09-22.
 *
 * İki yerde AYNEN kullanılır: ön kapının gösterdiği bağımsız `bakim.html` ve uygulama içi katman.
 * Kök hata görünümü gibi (boot-error-boundary.tsx) değerler burada SABİT yazılır: kesinti
 * sırasında CSS katmanları ya da kabuk hiç yüklenmemiş olabilir.
 */
const T = {
  bg: '#F3F5F9',
  card: '#FFFFFF',
  line: '#E4E8F0',
  line2: '#CBD3E0',
  track: '#E6EAF1',
  fg: '#0F172A',
  fg1: '#334155',
  fg2: '#64748B',
  fg3: '#94A3B8',
  accent: '#1A1B1D',
  accentSoft: '#E6E6E3',
  peachSoft: '#FDE6DA',
  warn: '#D9930F',
  warnSoft: '#FCEFD0',
  warnInk: '#8A5B06',
  font: '"Plus Jakarta Sans", -apple-system, "Segoe UI", system-ui, sans-serif',
};

/**
 * Hareket kuralı (styles/reduced-motion.css ile aynı): süsleme (dalga, nefes, arka plan
 * kayması, giriş) hareket azaltma tercihinde durur; DURUM bildiren ilerleme çizgisi kalır.
 */
const CSS = `
.bkm { position: relative; min-height: 100%; width: 100%; display: grid; place-items: center; overflow: hidden; background: ${T.bg}; color: ${T.fg}; font-family: ${T.font}; padding: 24px; box-sizing: border-box; }
.bkm *, .bkm *::before, .bkm *::after { box-sizing: border-box; }
.bkm-blob { position: absolute; width: 520px; height: 520px; border-radius: 50%; filter: blur(90px); opacity: .55; pointer-events: none; }
.bkm-blob.a { background: ${T.accentSoft}; top: -160px; right: -120px; animation: bkm-drift 14s ease-in-out infinite alternate; }
.bkm-blob.b { background: ${T.peachSoft}; bottom: -200px; left: -160px; animation: bkm-drift 18s ease-in-out infinite alternate-reverse; }
.bkm-grid { position: absolute; inset: 0; pointer-events: none; opacity: .5; background-image: linear-gradient(${T.line} 1px, transparent 1px), linear-gradient(90deg, ${T.line} 1px, transparent 1px); background-size: 40px 40px; -webkit-mask-image: radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 100%); mask-image: radial-gradient(ellipse 60% 60% at 50% 50%, #000 30%, transparent 100%); }
.bkm-body { position: relative; width: 100%; max-width: 360px; display: flex; flex-direction: column; align-items: center; gap: 16px; text-align: center; animation: bkm-in .5s cubic-bezier(.2,.8,.2,1) both; }
.bkm-halo { position: relative; width: 132px; height: 132px; display: grid; place-items: center; }
.bkm-wave { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(26,27,29,.18); animation: bkm-ripple 2.8s cubic-bezier(.2,.8,.2,1) infinite; }
.bkm-wave:nth-child(2) { animation-delay: .93s; }
.bkm-wave:nth-child(3) { animation-delay: 1.86s; }
.bkm-mark { position: relative; width: 68px; height: 68px; border-radius: 50%; background: ${T.card}; box-shadow: 0 1px 2px rgba(15,23,42,.08), inset 0 0 0 1px ${T.line}; overflow: hidden; display: grid; place-items: center; animation: bkm-breath 2.8s ease-in-out infinite; }
.bkm-mark img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
.bkm-mono { width: 38px; height: 38px; border-radius: 10px; background: ${T.accent}; color: #fff; display: grid; place-items: center; font: 700 18px/1 ${T.font}; }
.bkm-pill { display: inline-flex; align-items: center; gap: 7px; height: 24px; padding: 0 10px; border-radius: 999px; background: ${T.warnSoft}; color: ${T.warnInk}; font-size: 11.5px; font-weight: 600; letter-spacing: .01em; }
.bkm-pill i { width: 6px; height: 6px; border-radius: 50%; background: ${T.warn}; animation: bkm-blink 1.6s ease-in-out infinite; }
.bkm h1 { margin: 0; font-size: 20px; line-height: 1.25; font-weight: 600; letter-spacing: -.02em; }
.bkm-text { margin: 0; max-width: 300px; font-size: 13px; line-height: 1.55; color: ${T.fg2}; }
.bkm-facts { width: 300px; max-width: 100%; display: flex; border-top: 1px solid ${T.line}; border-bottom: 1px solid ${T.line}; }
.bkm-fact { flex: 1; min-width: 0; padding: 10px 6px; display: flex; flex-direction: column; gap: 3px; }
.bkm-fact + .bkm-fact { border-left: 1px solid ${T.line}; }
.bkm-fact dt { font-size: 10.5px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: ${T.fg3}; }
.bkm-fact dd { margin: 0; font-size: 13.5px; font-weight: 600; font-variant-numeric: tabular-nums; color: ${T.fg}; }
.bkm-quote { width: 300px; max-width: 100%; margin: 0; padding-left: 12px; border-left: 2px solid ${T.line2}; text-align: left; font-size: 12.5px; line-height: 1.5; color: ${T.fg1}; white-space: pre-line; }
.bkm-quote span { display: block; margin-bottom: 2px; font-size: 10.5px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: ${T.fg3}; }
.bkm-bar { position: relative; width: 220px; height: 4px; border-radius: 4px; background: ${T.track}; overflow: hidden; }
.bkm-bar::after { content: ""; position: absolute; inset: 0; width: 40%; border-radius: 4px; background: ${T.accent}; animation: bkm-slide 1.6s cubic-bezier(.4,0,.2,1) infinite; }
.bkm-meta { font-size: 11.5px; color: ${T.fg3}; }
@keyframes bkm-in { from { opacity: 0; transform: translateY(8px) scale(.985); } to { opacity: 1; transform: none; } }
@keyframes bkm-ripple { 0% { transform: scale(.55); opacity: .9; } 100% { transform: scale(1.25); opacity: 0; } }
@keyframes bkm-breath { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.04); } }
@keyframes bkm-drift { to { transform: translate(-30px, 24px) scale(1.08); } }
@keyframes bkm-blink { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
@keyframes bkm-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(260%); } }
@media (prefers-reduced-motion: reduce) {
  .bkm-blob, .bkm-wave, .bkm-mark, .bkm-body, .bkm-pill i { animation: none; }
  .bkm-wave { display: none; }
}
@media (max-width: 420px) {
  .bkm h1 { font-size: 18px; }
  .bkm-halo { width: 116px; height: 116px; }
}
`;

const TIME = new Intl.DateTimeFormat('tr-TR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Istanbul',
});

/** Kalan dakika yukarı yuvarlanır: 6 dk 50 sn "7 dk" okunur, "0 dk" hiç yazılmaz. */
function minutesLeft(endIso: string, now: number): number {
  return Math.ceil((Date.parse(endIso) - now) / 60_000);
}

function Mark() {
  const [failed, setFailed] = useState(false);
  return (
    <div className="bkm-halo" aria-hidden="true">
      <span className="bkm-wave" />
      <span className="bkm-wave" />
      <span className="bkm-wave" />
      <div className="bkm-mark">
        {failed ? (
          <span className="bkm-mono">D</span>
        ) : (
          <img src={BRAND_MARK} alt="" width={68} height={68} onError={() => setFailed(true)} />
        )}
      </div>
    </div>
  );
}

export type MaintenanceView = 'kesinti' | 'planli';
/** `public`: hastanın açtığı sayfalar (giriş, online randevu, ödeme). Klinik içi not gösterilmez. */
export type MaintenanceAudience = 'clinic' | 'public';

export function MaintenanceScreen({
  view,
  planned = null,
  now,
  audience,
}: {
  view: MaintenanceView;
  planned?: BakimDurumu | null;
  /** Sunucu saatine göre şimdi (ms); kalan süre bununla hesaplanır. */
  now: number;
  audience: MaintenanceAudience;
}) {
  const info = view === 'planli' ? planned : null;
  const left = info?.tahminiBitis ? minutesLeft(info.tahminiBitis, now) : null;
  const overdue = left !== null && left <= 0;
  return (
    <main className="bkm" aria-labelledby="bkm-title">
      <style>{CSS}</style>
      <div className="bkm-blob a" aria-hidden="true" />
      <div className="bkm-blob b" aria-hidden="true" />
      <div className="bkm-grid" aria-hidden="true" />
      <div className="bkm-body">
        <Mark />
        {info && (
          <span className="bkm-pill">
            <i aria-hidden="true" />
            Planlı bakım
          </span>
        )}
        {/* Hastanın gördüğü ekranda satıcı adı geçmez (2 Ekim 2026); personel DENT-OS'u tanır. */}
        <h1 id="bkm-title">
          {info
            ? audience === 'public'
              ? 'Planlı bakım yapılıyor'
              : 'DENT-OS bakımda'
            : 'Kısa bir bakım yapıyoruz'}
        </h1>
        {info ? (
          <dl className="bkm-facts" aria-live="polite">
            <div className="bkm-fact">
              <dt>Başladı</dt>
              <dd>{TIME.format(Date.parse(info.baslangic))}</dd>
            </div>
            {info.tahminiBitis && (
              <div className="bkm-fact">
                <dt>Tahmini dönüş</dt>
                <dd>
                  {TIME.format(Date.parse(info.tahminiBitis))}
                  {!overdue && left !== null ? ` · ${left} dk` : ''}
                </dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="bkm-text">
            {audience === 'public' ? 'Sayfa' : 'DENT-OS'} birkaç dakika içinde geri dönecek. Hazır
            olduğunda bu sayfa kendiliğinden açılır.
          </p>
        )}
        {info?.not && audience === 'clinic' && (
          <p className="bkm-quote">
            <span>Yönetici notu</span>
            {info.not}
          </p>
        )}
        <div
          className="bkm-bar"
          role="progressbar"
          aria-label="Sistem hazırlanıyor"
          aria-valuetext="Devam ediyor"
        />
        {info && (
          <div className="bkm-meta" aria-live="polite">
            {overdue
              ? 'Tahmini süre aşıldı, az kaldı'
              : 'Hazır olduğunda bu sayfa kendiliğinden açılır'}
          </div>
        )}
      </div>
    </main>
  );
}
